import type { ApiClient } from "@src/packages/api/louise-api";
import { throwApiError } from "@src/packages/api/api-error";
import type { paths } from "@src/packages/openapi/schema";
import type {
  CreateUploadJobRequest,
  AttachUploadJobSourceUploadRequest,
  GetUploadJobEventsResponse,
  UploadJobCommand,
  UploadJobCommandResponse,
  UploadJobEvent,
  UploadJobSnapshot,
  UploadJobIntent,
  ListUploadJobsResponse,
} from "./uploadJobContract";
import { isUploadJobEventType } from "./uploadJobContract";
import type { DocumentProcessingItem } from "./documentProcessingActivity";

const POLL_LIMIT = 100;

type ApiUploadJobEvent =
  paths["/patients/{patientId}/upload-jobs/{jobId}/events"]["get"]["responses"][200]["content"]["application/json"]["data"]["events"][number];

export type UploadJobEventEnvelope = {
  eventId: string;
  jobId: string;
  sequence: number;
  occurredAt: string;
  version: number;
  type: string;
  fileItemId?: string | null;
  payload: Record<string, unknown>;
};

function unwrap<T>(
  data: { data?: T } | undefined,
  error: unknown,
  fallback: string,
  context: string,
  status: number | undefined,
): T {
  if (data?.data !== undefined) return data.data;
  throwApiError(error, fallback, context, status);
}

export async function listUploadJobs(
  client: ApiClient,
  patientId: string,
  options: { includeCompleted?: boolean } = {},
): Promise<ListUploadJobsResponse> {
  const { data, error, response } = await client.GET(
    "/patients/{patientId}/upload-jobs",
    {
      params: {
        path: { patientId },
        query: {
          includeCompleted: options.includeCompleted ?? false,
          limit: POLL_LIMIT,
        },
      },
    },
  );
  return unwrap(
    data,
    error,
    "Could not load document activity",
    "listUploadJobs",
    response?.status,
  );
}

export async function getUploadJobEvents(
  client: ApiClient,
  patientId: string,
  jobId: string,
  afterSequence: number,
): Promise<GetUploadJobEventsResponse> {
  const {
    data,
    error,
    response: httpResponse,
  } = await client.GET("/patients/{patientId}/upload-jobs/{jobId}/events", {
    params: {
      path: { patientId, jobId },
      query: { afterSequence, limit: POLL_LIMIT },
    },
  });
  const response = unwrap(
    data,
    error,
    "Could not refresh document activity",
    "getUploadJobEvents",
    httpResponse?.status,
  );
  return {
    ...response,
    events: response.events.map((event: ApiUploadJobEvent) =>
      normalizeUploadJobEvent(event),
    ),
  };
}

export async function getUploadJobSnapshot(
  client: ApiClient,
  patientId: string,
  jobId: string,
): Promise<UploadJobSnapshot> {
  const { data, error, response } = await client.GET(
    "/patients/{patientId}/upload-jobs/{jobId}",
    { params: { path: { patientId, jobId } } },
  );
  return unwrap(
    data,
    error,
    "Could not refresh document activity",
    "getUploadJobSnapshot",
    response?.status,
  );
}

export async function createUploadJob(
  client: ApiClient,
  patientId: string,
  body: CreateUploadJobRequest,
): Promise<UploadJobSnapshot> {
  const { data, error, response } = await client.POST(
    "/patients/{patientId}/upload-jobs",
    {
      params: { path: { patientId } },
      body,
    },
  );
  return unwrap(
    data,
    error,
    "Could not start document upload",
    "createUploadJob",
    response?.status,
  );
}

export async function attachUploadJobSourceUpload(
  client: ApiClient,
  patientId: string,
  jobId: string,
  fileItemId: string,
  body: AttachUploadJobSourceUploadRequest,
): Promise<UploadJobSnapshot> {
  const { data, error, response } = await client.POST(
    "/patients/{patientId}/upload-jobs/{jobId}/items/{fileItemId}/source-upload",
    {
      params: { path: { patientId, jobId, fileItemId } },
      body,
    },
  );
  return unwrap(
    data,
    error,
    "Could not save document upload progress",
    "attachUploadJobSourceUpload",
    response?.status,
  );
}

export async function sendUploadJobCommand(
  client: ApiClient,
  patientId: string,
  jobId: string,
  command: UploadJobCommand,
): Promise<UploadJobCommandResponse> {
  const { data, error, response } = await client.POST(
    "/patients/{patientId}/upload-jobs/{jobId}/commands",
    {
      params: { path: { patientId, jobId } },
      body: command,
    },
  );
  return unwrap(
    data,
    error,
    "Could not update document activity",
    "sendUploadJobCommand",
    response?.status,
  );
}

/**
 * dashboard-api validates these event records at the route boundary. The
 * handwritten patient client cannot express the backend's generic payload
 * record as the reducer's discriminated union, so normalize the small set of
 * shared fields here before replaying it.
 */
export function normalizeUploadJobEvent(
  event: UploadJobEventEnvelope,
): UploadJobEvent {
  if (
    !isUploadJobEventType(event.type) ||
    !event.eventId ||
    !event.jobId ||
    !Number.isInteger(event.sequence) ||
    event.sequence < 1 ||
    !Number.isInteger(event.version) ||
    event.version < 1
  ) {
    throw new Error("Upload job returned an invalid event");
  }

  return event as UploadJobEvent;
}

function formatBytes(sizeBytes: number | null) {
  if (sizeBytes == null) return null;
  if (sizeBytes < 1_000_000) {
    return `${Math.max(1, Math.round(sizeBytes / 1_000))} KB`;
  }
  return `${(sizeBytes / 1_000_000).toFixed(sizeBytes >= 10_000_000 ? 0 : 1)} MB`;
}

function itemDetail(
  item: UploadJobSnapshot["items"][number],
  intent?: UploadJobIntent,
) {
  if (item.failure) {
    return item.failure.retryable
      ? "Action needed · Retry available"
      : "Action needed";
  }

  const size = formatBytes(item.sizeBytes);
  const progress = item.progress?.percent;
  switch (item.state) {
    case "queued":
      return size ? `${size} · Next up` : "Next up";
    case "uploading":
      return progress == null
        ? size
          ? `${size} · Uploading`
          : "Uploading"
        : `Uploading ${progress}%`;
    case "uploaded":
      return "Upload complete · Preparing to read";
    case "processing":
      return "Reading your document";
    case "background_processing":
      return "Processing in the background";
    case "review_ready":
      return intent === "dump_anything" ? "Processed" : "Ready to review";
    case "needs_review":
      return intent === "dump_anything" ? "Processed" : "Review needed";
    case "saving":
      return "Saving to your records";
    case "source_only":
      return "Original file saved";
    case "partial_success":
      return "Some information needs attention";
    case "complete":
      return "Saved to your records";
    case "canceled":
      return "Upload canceled";
    case "validating":
      return "Checking file type and size";
    default:
      return size ?? "Preparing your document";
  }
}

/** Converts durable file snapshots into the existing global activity model. */
export function uploadJobsToProcessingItems(
  jobs: readonly UploadJobSnapshot[],
): DocumentProcessingItem[] {
  return jobs
    .filter((job) => !job.acknowledgedAt)
    .flatMap((job) =>
      job.items.map((item) => ({
        id: item.id,
        name: item.name,
        detail: itemDetail(item, job.intent),
        state: item.state,
        progress: item.progress?.percent ?? undefined,
        ...(item.failure ? { failure: item.failure } : {}),
        durable: true,
        intent: job.intent,
        jobId: job.id,
        fileItemId: item.id,
      })),
    );
}

export function createUploadJobIdempotencyKey() {
  const runtimeCrypto = globalThis.crypto;
  if (runtimeCrypto?.randomUUID) {
    return runtimeCrypto.randomUUID();
  }
  // dashboard-api validates clientRequestId as a UUID. Keep the fallback
  // valid for older WebViews that expose neither randomUUID nor getRandomValues.
  const randomHex = () =>
    Math.floor(Math.random() * 0x10000)
      .toString(16)
      .padStart(4, "0");
  const variant = (8 + Math.floor(Math.random() * 4)).toString(16);
  return `${randomHex()}${randomHex()}${randomHex()}${randomHex()}-${randomHex()}-4${randomHex().slice(1)}-${variant}${randomHex().slice(1)}-${randomHex()}${randomHex()}${randomHex()}`;
}
